<template>
  <div id="app" class="container-fluid">
    <!-- Header with Patient Info -->
    <full_head />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Present Pregnancy Table -->
      <div class="card">
        <div class="card-header">
          <h3><i class="fas fa-baby"></i> Present Pregnancy Details</h3>
          <button class="btn-add" @click="openAddModal">
            <i class="fas fa-plus"></i> Add Record
          </button>
        </div>
        <div class="card-body">
          <div class="table-responsive">
            <table class="table">
              <thead>
                <tr>
                  <th>Visit Date</th>
                  <th>Gestation (Weeks)</th>
                  <th>Weight (kg)</th>
                  <th>BP</th>
                  <th>Fundal Height</th>
                  <th>Mother's Height</th>
                  <th>Fetal Heart</th>
                  <th>Presentation</th>
                  <th>Urine Test</th>
                  <th>Complaints</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="record in sortedRecords" :key="record.id">
                  <td>{{ formatDate(record.visit_date) }}</td>
                  <td>
                    <span class="gestation-badge"
                      >{{ record.gestation }} wks</span
                    >
                  </td>
                  <td>{{ record.weight }}</td>
                  <td>
                    <span
                      :class="
                        getBPClass(record.bp_systolic, record.bp_diastolic)
                      "
                    >
                      {{ record.bp_systolic }}/{{ record.bp_diastolic }}
                    </span>
                  </td>
                  <td>
                    {{ record.fundal_height || "-" }}
                    {{ record.fundal_height ? "cm" : "" }}
                  </td>
                  <td>
                    {{ record.height || "-" }}
                    {{ record.height ? "cm" : "" }}
                  </td>
                  <td>
                    <span :class="getFHRClass(record.fetal_heart_rate)">
                      {{ record.fetal_heart_rate || "-" }}
                      {{ record.fetal_heart_rate ? "bpm" : "" }}
                    </span>
                  </td>
                  <td>
                    <span
                      class="presentation-tag"
                      :class="getPresentationClass(record.presentation)"
                    >
                      {{ record.presentation || "-" }}
                    </span>
                  </td>
                  <td>
                    <span
                      class="urine-tag"
                      :class="getUrineClass(record.urine_test)"
                    >
                      {{ record.urine_test || "-" }}
                    </span>
                  </td>
                  <td>
                    <span
                      v-if="record.complaints"
                      class="complaints-text"
                      :title="record.complaints"
                    >
                      {{ truncateText(record.complaints, 20) }}
                    </span>
                    <span v-else class="text-muted">None</span>
                  </td>
                  <td class="actions">
                    <button class="btn-action edit" @click="editRecord(record)">
                      <i class="fas fa-edit"></i>
                    </button>
                    <button
                      class="btn-action delete"
                      @click="deleteRecord(record.id)"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                    <button
                      class="btn-action view"
                      @click="viewDetails(record)"
                      title="View Details"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="pregnancyRecords.length === 0">
                  <td colspan="10" class="text-center text-muted">
                    No pregnancy records found. Click "Add Record" to add the
                    first visit.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Summary Stats -->
          <div class="summary-stats">
            <div class="stat-card">
              <div class="stat-icon">
                <i class="fas fa-calendar-check"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ pregnancyRecords.length }}</div>
                <div class="stat-label">Total Visits</div>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-icon">
                <i class="fas fa-weight"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ getLastWeight() }}</div>
                <div class="stat-label">Current Weight</div>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-icon">
                <i class="fas fa-heartbeat"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ getLastBP() }}</div>
                <div class="stat-label">Current BP</div>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-icon">
                <i class="fas fa-clock"></i>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ getCurrentGestation() }}</div>
                <div class="stat-label">Gestation</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Quick Assessment -->
      <div class="card">
        <div class="card-header">
          <h3><i class="fas fa-clipboard-check"></i> Quick Assessment</h3>
        </div>
        <div class="card-body">
          <div class="assessment-grid">
            <div class="assessment-item" :class="getRiskAssessmentClass()">
              <div class="assessment-icon">
                <i class="fas fa-exclamation-triangle"></i>
              </div>
              <div class="assessment-content">
                <h5>Risk Assessment</h5>
                <p>{{ getRiskAssessment() }}</p>
              </div>
            </div>

            <div class="assessment-item">
              <div class="assessment-icon">
                <i class="fas fa-calendar-alt"></i>
              </div>
              <div class="assessment-content">
                <h5>Next Visit Due</h5>
                <p>{{ getNextVisitDue() }}</p>
              </div>
            </div>

            <div class="assessment-item">
              <div class="assessment-icon">
                <i class="fas fa-baby"></i>
              </div>
              <div class="assessment-content">
                <h5>Fetal Well-being</h5>
                <p>{{ getFetalWellbeing() }}</p>
              </div>
            </div>

            <div class="assessment-item">
              <div class="assessment-icon">
                <i class="fas fa-notes-medical"></i>
              </div>
              <div class="assessment-content">
                <h5>Required Interventions</h5>
                <p>{{ getRequiredInterventions() }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal Component -->
    <PregnancyVisitModal
      v-if="showAddModal"
      :visitId="visitId"
      :patientId="patientId"
      :companyId="companyId"
      :editRecord="editingRecordData"
      :lastGestation="lastGestation"
      :lastWeight="lastWeight"
      @close="closeAddModal"
      @saved="handleSaved"
    />

    <!-- View Details Modal -->
    <div v-if="showDetailsModal" class="modal-overlay">
      <div class="modal-content">
        <div class="modal-header">
          <h3>Visit Details - {{ formatDate(viewingRecord.visit_date) }}</h3>
          <button class="modal-close" @click="showDetailsModal = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="details-grid">
            <div class="detail-section">
              <h5>Vital Signs</h5>
              <div class="detail-item">
                <span class="label">Gestation:</span>
                <span class="value">{{ viewingRecord.gestation }} weeks</span>
              </div>
              <div class="detail-item">
                <span class="label">Weight:</span>
                <span class="value">{{ viewingRecord.weight }} kg</span>
              </div>
              <div class="detail-item">
                <span class="label">Blood Pressure:</span>
                <span
                  class="value"
                  :class="
                    getBPClass(
                      viewingRecord.bp_systolic,
                      viewingRecord.bp_diastolic,
                    )
                  "
                >
                  {{ viewingRecord.bp_systolic }}/{{
                    viewingRecord.bp_diastolic
                  }}
                  mmHg
                </span>
              </div>
            </div>

            <div class="detail-section">
              <h5>Fetal Assessment</h5>
              <div class="detail-item">
                <span class="label">Fundal Height:</span>
                <span class="value"
                  >{{ viewingRecord.fundal_height || "Not recorded" }} cm</span
                >
              </div>
              <div class="detail-item">
                <span class="label">Fetal Heart Rate:</span>
                <span
                  class="value"
                  :class="getFHRClass(viewingRecord.fetal_heart_rate)"
                >
                  {{ viewingRecord.fetal_heart_rate || "Not recorded" }} bpm
                </span>
              </div>
              <div class="detail-item">
                <span class="label">Presentation:</span>
                <span class="value">{{
                  viewingRecord.presentation || "Not recorded"
                }}</span>
              </div>
            </div>

            <div class="detail-section full-width">
              <h5>Tests & Findings</h5>
              <div class="detail-item">
                <span class="label">Urine Test:</span>
                <span
                  class="value urine-tag"
                  :class="getUrineClass(viewingRecord.urine_test)"
                >
                  {{ viewingRecord.urine_test || "Not done" }}
                </span>
              </div>
              <div v-if="viewingRecord.complaints" class="detail-item">
                <span class="label">Complaints:</span>
                <span class="value">{{ viewingRecord.complaints }}</span>
              </div>
              <div v-if="viewingRecord.examination" class="detail-item">
                <span class="label">Examination Findings:</span>
                <span class="value">{{ viewingRecord.examination }}</span>
              </div>
            </div>

            <div class="detail-section full-width">
              <h5>Management</h5>
              <div class="detail-item">
                <span class="label">Management/Advice:</span>
                <span class="value">{{
                  viewingRecord.management || "Not specified"
                }}</span>
              </div>
              <div v-if="viewingRecord.next_visit_date" class="detail-item">
                <span class="label">Next Visit:</span>
                <span class="value">{{
                  formatDate(viewingRecord.next_visit_date)
                }}</span>
              </div>
              <div v-if="viewingRecord.seen_by" class="detail-item">
                <span class="label">Seen By:</span>
                <span class="value">{{ viewingRecord.seen_by }}</span>
              </div>
            </div>
          </div>

          <div class="modal-actions">
            <button
              type="button"
              class="btn btn-secondary"
              @click="showDetailsModal = false"
            >
              Close
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="editRecordFromView"
            >
              Edit Record
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import full_head from "./full_head.vue";
import PregnancyVisitModal from "./PregnancyVisitModal.vue";
import { generalApiRequest } from "../../../../api/apiIndex";

export default {
  name: "PresentPregnancy",
  components: {
    full_head,
    PregnancyVisitModal,
  },
  data() {
    return {
      showAddModal: false,
      showDetailsModal: false,
      editingRecordData: null,
      viewingRecord: null,
      pregnancyRecords: [],
      visitId: this.$route.params.visit_id || null,
      patientId: null,
      companyId: null,
    };
  },
  computed: {
    sortedRecords() {
      return [...this.pregnancyRecords].sort(
        (a, b) => new Date(b.visit_date) - new Date(a.visit_date),
      );
    },
    lastGestation() {
      if (this.pregnancyRecords.length === 0) return null;
      const lastRecord = this.sortedRecords[0];
      return lastRecord.gestation || 12;
    },
    lastWeight() {
      if (this.pregnancyRecords.length === 0) return null;
      const lastRecord = this.sortedRecords[0];
      return lastRecord.weight || 60;
    },
  },
  mounted() {
    this.visitId =
      this.$route.params.visit_id || this.$route.params.visitId || null;
    // Get patient and company from parent component, store, or props
    this.patientId =
      this.$route.params.patient_id || this.$route.params.patientId || null;
    this.companyId =
      this.$route.params.company_id || this.$route.params.companyId || null;
    this.fetchPregnancyData();
  },
  methods: {
    async fetchPregnancyData() {
      if (!this.visitId) return;
      try {
        const response = await generalApiRequest(
          `anc_pregnancy_data/visit/${this.visitId}`,
          "GET",
          {},
          1,
        );
        if (response.success) {
          this.pregnancyRecords = response.data || [];
        }
      } catch (error) {
        console.error("Error fetching pregnancy data:", error);
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    truncateText(text, length) {
      if (!text) return "";
      return text.length > length ? text.substring(0, length) + "..." : text;
    },

    getBPClass(systolic, diastolic) {
      if (!systolic || !diastolic) return "";
      if (systolic >= 140 || diastolic >= 90) return "bp-high";
      if (systolic >= 130 || diastolic >= 85) return "bp-elevated";
      if (systolic < 90 || diastolic < 60) return "bp-low";
      return "bp-normal";
    },

    getFHRClass(fhr) {
      if (!fhr) return "";
      if (fhr < 110) return "fhr-low";
      if (fhr > 160) return "fhr-high";
      return "fhr-normal";
    },

    getPresentationClass(presentation) {
      const classes = {
        Cephalic: "presentation-cephalic",
        Breech: "presentation-breech",
        Transverse: "presentation-transverse",
        Oblique: "presentation-oblique",
        Unengaged: "presentation-unengaged",
      };
      return classes[presentation] || "";
    },

    getUrineClass(urineTest) {
      if (!urineTest || urineTest === "Normal") return "urine-normal";
      if (urineTest.includes("Protein")) return "urine-protein";
      if (urineTest.includes("Glucose")) return "urine-glucose";
      if (urineTest === "Infection") return "urine-infection";
      return "";
    },

    classifyBP(systolic, diastolic) {
      if (!systolic || !diastolic) return "Not recorded";
      if (systolic >= 140 || diastolic >= 90) return "Hypertensive";
      if (systolic >= 130 || diastolic >= 85) return "Elevated";
      if (systolic < 90 || diastolic < 60) return "Low";
      return "Normal";
    },

    classifyFHR(fhr) {
      if (!fhr) return "Not recorded";
      if (fhr < 110) return "Bradycardia";
      if (fhr > 160) return "Tachycardia";
      return "Normal";
    },

    getLastWeight() {
      if (this.pregnancyRecords.length === 0) return "-";
      const lastRecord = this.sortedRecords[0];
      return `${lastRecord.weight || "-"} kg`;
    },

    getLastBP() {
      if (this.pregnancyRecords.length === 0) return "-";
      const lastRecord = this.sortedRecords[0];
      return `${lastRecord.bp_systolic || "-"}/${
        lastRecord.bp_diastolic || "-"
      }`;
    },

    getCurrentGestation() {
      if (this.pregnancyRecords.length === 0) return "-";
      const lastRecord = this.sortedRecords[0];
      return `${lastRecord.gestation || "-"} wks`;
    },

    getRiskAssessment() {
      if (this.pregnancyRecords.length === 0) return "No records available";
      const lastRecord = this.sortedRecords[0];
      const riskFactors = [];

      if (lastRecord.bp_systolic >= 140 || lastRecord.bp_diastolic >= 90) {
        riskFactors.push("Hypertension");
      }
      if (lastRecord.urine_test && lastRecord.urine_test.includes("Protein")) {
        riskFactors.push("Proteinuria");
      }
      if (
        lastRecord.fetal_heart_rate &&
        (lastRecord.fetal_heart_rate < 110 || lastRecord.fetal_heart_rate > 160)
      ) {
        riskFactors.push("Abnormal FHR");
      }

      if (riskFactors.length === 0) return "Low Risk";
      if (riskFactors.length === 1) return `Medium Risk (${riskFactors[0]})`;
      return `High Risk (${riskFactors.join(", ")})`;
    },

    getRiskAssessmentClass() {
      const assessment = this.getRiskAssessment();
      if (assessment.includes("High Risk")) return "risk-high";
      if (assessment.includes("Medium Risk")) return "risk-medium";
      return "risk-low";
    },

    getNextVisitDue() {
      if (this.pregnancyRecords.length === 0) return "Schedule first visit";
      const lastRecord = this.sortedRecords[0];
      if (lastRecord.next_visit_date) {
        const today = new Date();
        const nextVisit = new Date(lastRecord.next_visit_date);
        const daysDiff = Math.ceil((nextVisit - today) / (1000 * 60 * 60 * 24));

        if (daysDiff < 0) return `Overdue by ${Math.abs(daysDiff)} days`;
        if (daysDiff <= 7) return `Due in ${daysDiff} days`;
        return this.formatDate(lastRecord.next_visit_date);
      }
      return "Not scheduled";
    },

    getFetalWellbeing() {
      if (this.pregnancyRecords.length === 0) return "No fetal assessment";
      const lastRecord = this.sortedRecords[0];
      if (!lastRecord.fetal_heart_rate) return "Not assessed";

      if (
        lastRecord.fetal_heart_rate >= 110 &&
        lastRecord.fetal_heart_rate <= 160
      ) {
        return "Normal fetal heart rate";
      }
      return "Needs monitoring";
    },

    getRequiredInterventions() {
      if (this.pregnancyRecords.length === 0) return "First ANC visit needed";
      const lastRecord = this.sortedRecords[0];
      const interventions = [];

      if (lastRecord.gestation >= 28 && !lastRecord.urine_test) {
        interventions.push("Urine test");
      }
      if (lastRecord.bp_systolic >= 140 || lastRecord.bp_diastolic >= 90) {
        interventions.push("BP monitoring");
      }
      if (
        lastRecord.fetal_heart_rate &&
        (lastRecord.fetal_heart_rate < 110 || lastRecord.fetal_heart_rate > 160)
      ) {
        interventions.push("Fetal monitoring");
      }

      if (interventions.length === 0) return "No interventions required";
      return interventions.join(", ");
    },

    openAddModal() {
      this.editingRecordData = null;
      this.showAddModal = true;
    },

    closeAddModal() {
      this.showAddModal = false;
      this.editingRecordData = null;
    },

    editRecord(record) {
      this.editingRecordData = { ...record };
      this.showAddModal = true;
      this.showDetailsModal = false;
    },

    editRecordFromView() {
      if (this.viewingRecord) {
        this.editingRecordData = { ...this.viewingRecord };
        this.showAddModal = true;
        this.showDetailsModal = false;
      }
    },

    viewDetails(record) {
      this.viewingRecord = { ...record };
      this.showDetailsModal = true;
    },

    async deleteRecord(id) {
      if (!confirm("Are you sure you want to delete this pregnancy record?"))
        return;

      try {
        const response = await generalApiRequest(
          `anc_pregnancy_data/${id}`,
          "DELETE",
          null,
          1,
        );
        if (response.success) {
          this.pregnancyRecords = this.pregnancyRecords.filter(
            (record) => record.id !== id,
          );
        }
      } catch (error) {
        console.error("Error deleting record:", error);
      }
    },

    handleSaved(newRecord) {
      if (!newRecord) return;

      const index = this.pregnancyRecords.findIndex(
        (r) => r.id === newRecord.id,
      );
      if (index !== -1) {
        // Update existing record
        this.$set(this.pregnancyRecords, index, newRecord);
      } else {
        // Add new record
        this.pregnancyRecords.push(newRecord);
      }

      // Refresh data from server to ensure consistency
      this.fetchPregnancyData();
    },
  },
};
</script>

<style scoped>
.container-fluid {
  padding: 20px;
  background-color: #f8f9fa;
}

.main-content {
  margin-top: 20px;
}

.card {
  background: white;
  border-radius: 10px;
  margin-bottom: 25px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  border: none;
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px;
  border-bottom: 2px solid #dee2e6;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-header h3 {
  margin: 0;
  color: #495057;
  font-size: 20px;
  font-weight: 600;
}

.card-header h3 i {
  margin-right: 10px;
  color: #667eea;
}

.btn-add {
  padding: 8px 20px;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 600;
}

.btn-add:hover {
  background: #218838;
}

.card-body {
  padding: 25px;
}

.table-responsive {
  overflow-x: auto;
  margin-bottom: 30px;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th {
  background: #f8f9fa;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  white-space: nowrap;
}

.table td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
}

.gestation-badge {
  display: inline-block;
  padding: 4px 10px;
  background: #e8f4fd;
  color: #2980b9;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
}

.bp-normal {
  color: #27ae60;
  font-weight: 600;
}

.bp-elevated {
  color: #f39c12;
  font-weight: 600;
}

.bp-high {
  color: #e74c3c;
  font-weight: 600;
}

.bp-low {
  color: #3498db;
  font-weight: 600;
}

.fhr-normal {
  color: #27ae60;
  font-weight: 600;
}

.fhr-low,
.fhr-high {
  color: #e74c3c;
  font-weight: 600;
}

.presentation-tag {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.presentation-cephalic {
  background: #d4edda;
  color: #155724;
}

.presentation-breech {
  background: #fff3cd;
  color: #856404;
}

.presentation-transverse,
.presentation-oblique,
.presentation-unengaged {
  background: #f8d7da;
  color: #721c24;
}

.urine-tag {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.urine-normal {
  background: #d4edda;
  color: #155724;
}

.urine-protein {
  background: #f8d7da;
  color: #721c24;
}

.urine-glucose {
  background: #fff3cd;
  color: #856404;
}

.urine-infection {
  background: #d1ecf1;
  color: #0c5460;
}

.complaints-text {
  font-size: 12px;
  color: #6c757d;
  cursor: help;
}

.text-muted {
  color: #6c757d;
  font-style: italic;
}

.actions {
  display: flex;
  gap: 5px;
}

.btn-action {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-action.edit {
  background: #3498db;
  color: white;
}

.btn-action.edit:hover {
  background: #2980b9;
}

.btn-action.delete {
  background: #e74c3c;
  color: white;
}

.btn-action.delete:hover {
  background: #c0392b;
}

.btn-action.view {
  background: #6c757d;
  color: white;
}

.btn-action.view:hover {
  background: #5a6268;
}

.summary-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.stat-card {
  background: white;
  border-radius: 10px;
  padding: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #dee2e6;
}

.stat-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  font-size: 20px;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 5px;
}

.stat-label {
  color: #7f8c8d;
  font-size: 14px;
  font-weight: 600;
}

.assessment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.assessment-item {
  display: flex;
  gap: 15px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 10px;
  align-items: center;
  border-left: 5px solid #dee2e6;
}

.assessment-item.risk-low {
  border-left-color: #28a745;
}

.assessment-item.risk-medium {
  border-left-color: #ffc107;
}

.assessment-item.risk-high {
  border-left-color: #dc3545;
}

.assessment-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 20px;
  background: white;
}

.risk-low .assessment-icon {
  color: #28a745;
  border: 2px solid #28a745;
}

.risk-medium .assessment-icon {
  color: #ffc107;
  border: 2px solid #ffc107;
}

.risk-high .assessment-icon {
  color: #dc3545;
  border: 2px solid #dc3545;
}

.assessment-content {
  flex: 1;
}

.assessment-content h5 {
  margin: 0 0 8px 0;
  color: #2c3e50;
  font-size: 16px;
  font-weight: 600;
}

.assessment-content p {
  margin: 0;
  color: #6c757d;
  font-size: 14px;
  line-height: 1.5;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 10px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  padding: 20px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
}

.modal-close {
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
}

.modal-body {
  padding: 30px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.detail-section {
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.detail-section.full-width {
  grid-column: 1 / -1;
}

.detail-section h5 {
  margin: 0 0 15px 0;
  color: #495057;
  font-size: 16px;
  font-weight: 600;
  padding-bottom: 10px;
  border-bottom: 2px solid #dee2e6;
}

.detail-item {
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
}

.detail-item .label {
  font-weight: 600;
  color: #6c757d;
}

.detail-item .value {
  color: #2c3e50;
  text-align: right;
  max-width: 70%;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 20px;
}

.btn {
  padding: 10px 25px;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #5a6268;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover {
  background: #5a6fd8;
}

@media (max-width: 1200px) {
  .details-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 992px) {
  .summary-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  .assessment-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .container-fluid {
    padding: 10px;
  }

  .table {
    font-size: 14px;
  }

  .table th,
  .table td {
    padding: 8px;
  }

  .summary-stats {
    grid-template-columns: 1fr;
  }

  .assessment-grid {
    grid-template-columns: 1fr;
  }

  .card-header {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .btn-add {
    align-self: stretch;
    justify-content: center;
  }
}

@media (max-width: 576px) {
  .table-responsive {
    overflow-x: auto;
  }

  .table {
    min-width: 800px;
  }
}
</style>
